import "./otherOperationMenu.less";
import React, { useEffect } from "react";
import { Menu, Popover, Tooltip, MenuProps } from "antd";
import { StarFilled, StarOutlined } from "@ant-design/icons";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { observer } from "mobx-react-lite";
import PremiumVersionIcon from "@/components/PremiumVersionIcon";
import SplitOrderMenuItem from "./SplitOrderMenuItem";
import { Link } from "react-router-dom";
import { useGlobalStore } from "@/store/hooks";
import CopyOrderInfoSettingButton from "./CopyOrderInfoSettingButton";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import {
    OperationKey,
    OperationLabelNameMap,
    getAvailableOtherOperationKeys,
} from "../services/otherOperationDefinitions";
import { useOtherOperationActions } from "../hooks/useOtherOperationActions";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import { MenuDivider, MenuItem, MenuItemGroup, SubMenu, buildMenuItems } from "@/utils/antdItems";

export interface OtherOperationMenuProps extends MenuProps {
    tab: OrderTab;
    shop: ShopDTO;
    getSelectedOrders(): OrderDTO[];
    onLoadingChange?: (loading: boolean) => void;
    isSupplierShop: boolean;
}

const OtherOperationMenu: React.FC<OtherOperationMenuProps> = function (props) {
    const {
        tab,
        shop,
        getSelectedOrders,
        onLoadingChange,
        onClick: onMenuClick,
        isSupplierShop,
        ...otherProps
    } = props;

    const {
        handleMenuClick,
        isRetailer,
        fulfillmentType,
        showPackListExport,
        showBatchSubmitOrder,
        showBatchPayOrder,
    } = useOtherOperationActions({
        tab,
        shop,
        getSelectedOrders,
        onMenuClick,
        onLoadingChange,
    });

    const { companyId } = useGlobalStore();

    const availableKeys = getAvailableOtherOperationKeys({
        tab,
        shop,
        isSupplierShop,
        isRetailer,
        fulfillmentType,
        showPackListExport,
        showBatchSubmitOrder,
        showBatchPayOrder,
        companyId,
    });
    const availableSet = new Set(availableKeys);
    const hasKey = (key: OperationKey) => availableSet.has(key);

    const orderManagementStore = useOrderManagementStore();
    const { storage } = orderManagementStore;

    const favoriteKeys = storage.favoriteOperationKeysCache;

    const isFavorite = (key: OperationKey) => {
        return favoriteKeys.includes(key);
    };

    const renderFavoriteNode = (key: OperationKey) => {
        const active = isFavorite(key);

        return (
            <span
                className={`ec-other-operation-menu-favorite ${active ? "is-active" : ""}`}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    storage.toggleFavoriteOperationKey(key);

                    collectEvent(DataTrackEvent.LIKE_OPERATION, {
                        content: OperationLabelNameMap[key],
                        enabled: active ? "移除" : "新增",
                    });
                }}
                onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
            >
                {active ? <StarFilled /> : <StarOutlined />}
            </span>
        );
    };

    const renderMenuItemContent = (labelNode: React.ReactNode, key: OperationKey, extraNode?: React.ReactNode) => {
        return (
            <div className="ec-other-operation-menu-item">
                <span className="ec-other-operation-menu-item-label">{labelNode}</span>
                {extraNode}
                {renderFavoriteNode(key)}
            </div>
        );
    };

    if (tab === OrderTab.PRE_SHIP) {
        return (
            <Menu {...otherProps} onClick={handleMenuClick} className="ec-other-operation-dropdown-menu-container" items={buildMenuItems(<>
                {hasKey(OperationKey.EXPORT_PICK_LIST) ? (
                    <MenuItem key={OperationKey.EXPORT_PICK_LIST}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.EXPORT_PICK_LIST]}</span>,
                            OperationKey.EXPORT_PICK_LIST,
                        )}
                    </MenuItem>
                ) : null}
                {hasKey(OperationKey.EXPORT_ORDER) ? (
                    <MenuItem key={OperationKey.EXPORT_ORDER}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.EXPORT_ORDER]}</span>,
                            OperationKey.EXPORT_ORDER,
                        )}
                    </MenuItem>
                ) : null}
            </>)} />
        );
    }

    if (isSupplierShop) {
        return (
            <Menu {...otherProps} onClick={handleMenuClick} className="ec-other-operation-dropdown-menu-container" items={buildMenuItems(<>
                <MenuItemGroup title="常用操作">
                    {hasKey(OperationKey.HOLD_ORDER) ? (
                        <MenuItem key={OperationKey.HOLD_ORDER}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.HOLD_ORDER]}</span>,
                                OperationKey.HOLD_ORDER,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.BATCH_MERGE_ORDER) ? (
                        <MenuItem key={OperationKey.BATCH_MERGE_ORDER}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.BATCH_MERGE_ORDER]}</span>,
                                OperationKey.BATCH_MERGE_ORDER,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.BATCH_CANCEL_MERGE_ORDER) ? (
                        <MenuItem key={OperationKey.BATCH_CANCEL_MERGE_ORDER}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.BATCH_CANCEL_MERGE_ORDER]}</span>,
                                OperationKey.BATCH_CANCEL_MERGE_ORDER,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.SUPPLIER_BATCH_MODIFY_DROPSHIPPING_REMARK) ? (
                        <MenuItem key={OperationKey.SUPPLIER_BATCH_MODIFY_DROPSHIPPING_REMARK}>
                            {renderMenuItemContent(
                                <span>
                                    {OperationLabelNameMap[OperationKey.SUPPLIER_BATCH_MODIFY_DROPSHIPPING_REMARK]}
                                </span>,
                                OperationKey.SUPPLIER_BATCH_MODIFY_DROPSHIPPING_REMARK,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.ORDER_MARK) ? (
                        <MenuItem key={OperationKey.ORDER_MARK}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.ORDER_MARK]}</span>,
                                OperationKey.ORDER_MARK,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.SPLIT_ORDER) ? (
                        <MenuItem key={OperationKey.SPLIT_ORDER}>
                            <SplitOrderMenuItem
                                shop={shop}
                                favoriteNode={renderFavoriteNode(OperationKey.SPLIT_ORDER)}
                            />
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.CANCEL_SPLIT_ORDER) ? (
                        <MenuItem key={OperationKey.CANCEL_SPLIT_ORDER}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.CANCEL_SPLIT_ORDER]}</span>,
                                OperationKey.CANCEL_SPLIT_ORDER,
                                <PremiumVersionIcon />,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.SUPPLIER_BATCH_SYNC_ORDERS) ? (
                        <MenuItem key={OperationKey.SUPPLIER_BATCH_SYNC_ORDERS}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.SUPPLIER_BATCH_SYNC_ORDERS]}</span>,
                                OperationKey.SUPPLIER_BATCH_SYNC_ORDERS,
                            )}
                        </MenuItem>
                    ) : null}
                </MenuItemGroup>
                <MenuItemGroup title="打印发货操作">
                    {hasKey(OperationKey.PRINT_PACKING_LIST) ? (
                        <MenuItem key={OperationKey.PRINT_PACKING_LIST}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.PRINT_PACKING_LIST]}</span>,
                                OperationKey.PRINT_PACKING_LIST,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.PRE_SHIP) ? (
                        <MenuItem key={OperationKey.PRE_SHIP}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.PRE_SHIP]}</span>,
                                OperationKey.PRE_SHIP,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.REISSUE_PACKAGE) ? (
                        <MenuItem key={OperationKey.REISSUE_PACKAGE}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.REISSUE_PACKAGE]}</span>,
                                OperationKey.REISSUE_PACKAGE,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.RESHIP) ? (
                        <MenuItem key={OperationKey.RESHIP}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.RESHIP]}</span>,
                                OperationKey.RESHIP,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.PRINT_ORDER_ITEM_LABEL) ? (
                        <MenuItem key={OperationKey.PRINT_ORDER_ITEM_LABEL}>
                            {renderMenuItemContent(
                                <Popover
                                    zIndex={1100}
                                    content={
                                        <div
                                            style={{ width: 700 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            <Link to="/features/purchasingLabel">
                                                <img
                                                    style={{ width: "100%" }}
                                                    src="https://img.alicdn.com/imgextra/i3/54458674/O1CN01JESJiW2DwkPu919bu_!!54458674.png"
                                                    alt=""
                                                />
                                            </Link>
                                        </div>
                                    }
                                    placement="top"
                                >
                                    <span>{OperationLabelNameMap[OperationKey.PRINT_ORDER_ITEM_LABEL]}</span>
                                </Popover>,
                                OperationKey.PRINT_ORDER_ITEM_LABEL,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.BATCH_REPRINT_EWAYBILLS) ? (
                        <MenuItem key={OperationKey.BATCH_REPRINT_EWAYBILLS}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.BATCH_REPRINT_EWAYBILLS]}</span>,
                                OperationKey.BATCH_REPRINT_EWAYBILLS,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.BATCH_CANCEL_EWAYBILL_NOS) ? (
                        <MenuItem key={OperationKey.BATCH_CANCEL_EWAYBILL_NOS}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.BATCH_CANCEL_EWAYBILL_NOS]}</span>,
                                OperationKey.BATCH_CANCEL_EWAYBILL_NOS,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.BATCH_DELETE_EWAYBILL_NOS) ? (
                        <MenuItem key={OperationKey.BATCH_DELETE_EWAYBILL_NOS}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.BATCH_DELETE_EWAYBILL_NOS]}</span>,
                                OperationKey.BATCH_DELETE_EWAYBILL_NOS,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.UPDATE_LOGISTICS_COMPANY) ? (
                        <MenuItem key={OperationKey.UPDATE_LOGISTICS_COMPANY}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.UPDATE_LOGISTICS_COMPANY]}</span>,
                                OperationKey.UPDATE_LOGISTICS_COMPANY,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.UPDATE_PRINTING_STATUS) ? (
                        <MenuItem key={OperationKey.UPDATE_PRINTING_STATUS}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.UPDATE_PRINTING_STATUS]}</span>,
                                OperationKey.UPDATE_PRINTING_STATUS,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.AUTO_PRINT) ? (
                        <MenuItem key={OperationKey.AUTO_PRINT}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.AUTO_PRINT]}</span>,
                                OperationKey.AUTO_PRINT,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.INSTANT_SHIP) ? (
                        <MenuItem key={OperationKey.INSTANT_SHIP}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.INSTANT_SHIP]}</span>,
                                OperationKey.INSTANT_SHIP,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.SCAN_PRINT) ? (
                        <MenuItem key={OperationKey.SCAN_PRINT}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.SCAN_PRINT]}</span>,
                                OperationKey.SCAN_PRINT,
                            )}
                        </MenuItem>
                    ) : null}
                </MenuItemGroup>
                <MenuItemGroup title="导出操作">
                    {hasKey(OperationKey.EXPORT_PICK_LIST) ? (
                        <MenuItem key={OperationKey.EXPORT_PICK_LIST}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.EXPORT_PICK_LIST]}</span>,
                                OperationKey.EXPORT_PICK_LIST,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.EXPORT_ORDER) ? (
                        <MenuItem key={OperationKey.EXPORT_ORDER}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.EXPORT_ORDER]}</span>,
                                OperationKey.EXPORT_ORDER,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.EXPORT_EXPRESS) ? (
                        <MenuItem key={OperationKey.EXPORT_EXPRESS}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.EXPORT_EXPRESS]}</span>,
                                OperationKey.EXPORT_EXPRESS,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.EXPORT_PRINT_PURCHASING_LABEL) ? (
                        <MenuItem key={OperationKey.EXPORT_PRINT_PURCHASING_LABEL}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.EXPORT_PRINT_PURCHASING_LABEL]}</span>,
                                OperationKey.EXPORT_PRINT_PURCHASING_LABEL,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.DOWNLOAD_CUSTOMIZATION) ? (
                        <MenuItem key={OperationKey.DOWNLOAD_CUSTOMIZATION}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.DOWNLOAD_CUSTOMIZATION]}</span>,
                                OperationKey.DOWNLOAD_CUSTOMIZATION,
                            )}
                        </MenuItem>
                    ) : null}
                </MenuItemGroup>
                <MenuItemGroup title="分销操作">
                    {hasKey(OperationKey.CANCEL_DROPSHIPPING_ORDER) ? (
                        <MenuItem key={OperationKey.CANCEL_DROPSHIPPING_ORDER}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.CANCEL_DROPSHIPPING_ORDER]}</span>,
                                OperationKey.CANCEL_DROPSHIPPING_ORDER,
                            )}
                        </MenuItem>
                    ) : null}
                </MenuItemGroup>
                <MenuItemGroup title="其他操作">
                    {hasKey(OperationKey.BATCH_LOCK) ? (
                        <MenuItem key={OperationKey.BATCH_LOCK}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.BATCH_LOCK]}</span>,
                                OperationKey.BATCH_LOCK,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.BATCH_UNLOCK) ? (
                        <MenuItem key={OperationKey.BATCH_UNLOCK}>
                            {renderMenuItemContent(
                                <Tooltip
                                    placement="right"
                                    title="将当前页面所有已经锁定的订单批量解锁。手动锁定的订单解锁后不再锁定；不可达锁单、地址异常锁单和黑名单锁单解锁后再刷新依然锁定"
                                >
                                    <span>{OperationLabelNameMap[OperationKey.BATCH_UNLOCK]}</span>
                                </Tooltip>,
                                OperationKey.BATCH_UNLOCK,
                            )}
                        </MenuItem>
                    ) : null}
                    {hasKey(OperationKey.COPY_ORDER_INFO) ? (
                        <MenuItem key={OperationKey.COPY_ORDER_INFO}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.COPY_ORDER_INFO]}</span>,
                                OperationKey.COPY_ORDER_INFO,
                                <CopyOrderInfoSettingButton />,
                            )}
                        </MenuItem>
                    ) : null}
                </MenuItemGroup>
            </>)} />
        );
    }

    return (
        <Menu {...otherProps} className="ec-other-operation-dropdown-menu-container" onClick={handleMenuClick} items={buildMenuItems(<>
            <MenuItemGroup title="常用操作">
                {hasKey(OperationKey.HOLD_ORDER) ? (
                    <MenuItem key={OperationKey.HOLD_ORDER}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.HOLD_ORDER]}</span>,
                            OperationKey.HOLD_ORDER,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.BATCH_MERGE_ORDER) ? (
                    <MenuItem key={OperationKey.BATCH_MERGE_ORDER}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.BATCH_MERGE_ORDER]}</span>,
                            OperationKey.BATCH_MERGE_ORDER,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.BATCH_CANCEL_MERGE_ORDER) ? (
                    <MenuItem key={OperationKey.BATCH_CANCEL_MERGE_ORDER}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.BATCH_CANCEL_MERGE_ORDER]}</span>,
                            OperationKey.BATCH_CANCEL_MERGE_ORDER,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.UPDATE_SELLER_REMARKS) ? (
                    <MenuItem key={OperationKey.UPDATE_SELLER_REMARKS}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.UPDATE_SELLER_REMARKS]}</span>,
                            OperationKey.UPDATE_SELLER_REMARKS,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.ORDER_MARK) ? (
                    <MenuItem key={OperationKey.ORDER_MARK}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.ORDER_MARK]}</span>,
                            OperationKey.ORDER_MARK,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.SPLIT_ORDER) ? (
                    <MenuItem key={OperationKey.SPLIT_ORDER}>
                        <SplitOrderMenuItem shop={shop} favoriteNode={renderFavoriteNode(OperationKey.SPLIT_ORDER)} />
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.CANCEL_SPLIT_ORDER) ? (
                    <MenuItem key={OperationKey.CANCEL_SPLIT_ORDER}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.CANCEL_SPLIT_ORDER]}</span>,
                            OperationKey.CANCEL_SPLIT_ORDER,
                            <PremiumVersionIcon />,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.BATCH_SYNC_ORDERS) ? (
                    <MenuItem key={OperationKey.BATCH_SYNC_ORDERS}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.BATCH_SYNC_ORDERS]}</span>,
                            OperationKey.BATCH_SYNC_ORDERS,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.BATCH_UPDATE_IMEI) ? (
                    <MenuItem key={OperationKey.BATCH_UPDATE_IMEI}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.BATCH_UPDATE_IMEI]}</span>,
                            OperationKey.BATCH_UPDATE_IMEI,
                        )}
                    </MenuItem>
                ) : null}
            </MenuItemGroup>
            <MenuItemGroup title="打印发货操作">
                {hasKey(OperationKey.PRINT_PACKING_LIST) ? (
                    <MenuItem key={OperationKey.PRINT_PACKING_LIST}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.PRINT_PACKING_LIST]}</span>,
                            OperationKey.PRINT_PACKING_LIST,
                        )}
                    </MenuItem>
                ) : null}
                {hasKey(OperationKey.PRE_SHIP) ? (
                    <MenuItem key={OperationKey.PRE_SHIP}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.PRE_SHIP]}</span>,
                            OperationKey.PRE_SHIP,
                        )}
                    </MenuItem>
                ) : null}
                {hasKey(OperationKey.REISSUE_PACKAGE) ? (
                    <MenuItem key={OperationKey.REISSUE_PACKAGE}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.REISSUE_PACKAGE]}</span>,
                            OperationKey.REISSUE_PACKAGE,
                        )}
                    </MenuItem>
                ) : null}
                {hasKey(OperationKey.RESHIP) ? (
                    <MenuItem key={OperationKey.RESHIP}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.RESHIP]}</span>,
                            OperationKey.RESHIP,
                        )}
                    </MenuItem>
                ) : null}
                {hasKey(OperationKey.PRINT_ORDER_ITEM_LABEL) ? (
                    <MenuItem key={OperationKey.PRINT_ORDER_ITEM_LABEL}>
                        {renderMenuItemContent(
                            <Popover
                                zIndex={1100}
                                content={
                                    <div
                                        style={{ width: 700 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    >
                                        <Link to="/features/purchasingLabel">
                                            <img
                                                style={{ width: "100%" }}
                                                src="https://img.alicdn.com/imgextra/i3/54458674/O1CN01JESJiW2DwkPu919bu_!!54458674.png"
                                                alt=""
                                            />
                                        </Link>
                                    </div>
                                }
                                placement="top"
                            >
                                <span>{OperationLabelNameMap[OperationKey.PRINT_ORDER_ITEM_LABEL]}</span>
                            </Popover>,
                            OperationKey.PRINT_ORDER_ITEM_LABEL,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.BATCH_REPRINT_EWAYBILLS) ? (
                    <MenuItem key={OperationKey.BATCH_REPRINT_EWAYBILLS}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.BATCH_REPRINT_EWAYBILLS]}</span>,
                            OperationKey.BATCH_REPRINT_EWAYBILLS,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.BATCH_CANCEL_EWAYBILL_NOS) ? (
                    <MenuItem key={OperationKey.BATCH_CANCEL_EWAYBILL_NOS}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.BATCH_CANCEL_EWAYBILL_NOS]}</span>,
                            OperationKey.BATCH_CANCEL_EWAYBILL_NOS,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.BATCH_DELETE_EWAYBILL_NOS) ? (
                    <MenuItem key={OperationKey.BATCH_DELETE_EWAYBILL_NOS}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.BATCH_DELETE_EWAYBILL_NOS]}</span>,
                            OperationKey.BATCH_DELETE_EWAYBILL_NOS,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.UPDATE_LOGISTICS_COMPANY) ? (
                    <MenuItem key={OperationKey.UPDATE_LOGISTICS_COMPANY}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.UPDATE_LOGISTICS_COMPANY]}</span>,
                            OperationKey.UPDATE_LOGISTICS_COMPANY,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.UPDATE_PRINTING_STATUS) ? (
                    <MenuItem key={OperationKey.UPDATE_PRINTING_STATUS}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.UPDATE_PRINTING_STATUS]}</span>,
                            OperationKey.UPDATE_PRINTING_STATUS,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.PDD_FDS_EWAYBILL_CANCEL) ? (
                    <MenuItem key={OperationKey.PDD_FDS_EWAYBILL_CANCEL}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.PDD_FDS_EWAYBILL_CANCEL]}</span>,
                            OperationKey.PDD_FDS_EWAYBILL_CANCEL,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.QUALITY_INSPECTION_CODE) ? (
                    <MenuItem key={OperationKey.QUALITY_INSPECTION_CODE}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.QUALITY_INSPECTION_CODE]}</span>,
                            OperationKey.QUALITY_INSPECTION_CODE,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.AUTO_PRINT) ? (
                    <MenuItem key={OperationKey.AUTO_PRINT}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.AUTO_PRINT]}</span>,
                            OperationKey.AUTO_PRINT,
                        )}
                    </MenuItem>
                ) : null}
                {hasKey(OperationKey.INSTANT_SHIP) ? (
                    <MenuItem key={OperationKey.INSTANT_SHIP}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.INSTANT_SHIP]}</span>,
                            OperationKey.INSTANT_SHIP,
                        )}
                    </MenuItem>
                ) : null}
                {hasKey(OperationKey.SCAN_PRINT) ? (
                    <MenuItem key={OperationKey.SCAN_PRINT}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.SCAN_PRINT]}</span>,
                            OperationKey.SCAN_PRINT,
                        )}
                    </MenuItem>
                ) : null}
            </MenuItemGroup>
            <MenuItemGroup title="导出操作">
                {hasKey(OperationKey.EXPORT_PICK_LIST) ? (
                    <MenuItem key={OperationKey.EXPORT_PICK_LIST}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.EXPORT_PICK_LIST]}</span>,
                            OperationKey.EXPORT_PICK_LIST,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.EXPORT_ORDER) ? (
                    <MenuItem key={OperationKey.EXPORT_ORDER}>
                        {renderMenuItemContent(
                            <Popover
                                zIndex={1100}
                                placement="left"
                                content={() => {
                                    return (
                                        <span>
                                            需要导出订单给供货商/厂家发货的商家，可以使用分销代发功能，请联系客服咨询或者
                                            <a href="https://mp.weixin.qq.com/s/fEfVH5V7FthIo00LcIgk4A" target="_blank">
                                                查看代发方案介绍
                                            </a>
                                        </span>
                                    );
                                }}
                                styles={{ root: { width: "320px" } }}
                            >
                                <span>{OperationLabelNameMap[OperationKey.EXPORT_ORDER]}</span>
                            </Popover>,
                            OperationKey.EXPORT_ORDER,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.EXPORT_SHIPPING_TEMPLATE) ? (
                    <MenuItem key={OperationKey.EXPORT_SHIPPING_TEMPLATE}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.EXPORT_SHIPPING_TEMPLATE]}</span>,
                            OperationKey.EXPORT_SHIPPING_TEMPLATE,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.EXPORT_EXPRESS) ? (
                    <MenuItem key={OperationKey.EXPORT_EXPRESS}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.EXPORT_EXPRESS]}</span>,
                            OperationKey.EXPORT_EXPRESS,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.EXPORT_PRINT_PURCHASING_LABEL) ? (
                    <MenuItem key={OperationKey.EXPORT_PRINT_PURCHASING_LABEL}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.EXPORT_PRINT_PURCHASING_LABEL]}</span>,
                            OperationKey.EXPORT_PRINT_PURCHASING_LABEL,
                        )}
                    </MenuItem>
                ) : null}

                {hasKey(OperationKey.DOWNLOAD_CUSTOMIZATION) ? (
                    <MenuItem key={OperationKey.DOWNLOAD_CUSTOMIZATION}>
                        {renderMenuItemContent(
                            <span>{OperationLabelNameMap[OperationKey.DOWNLOAD_CUSTOMIZATION]}</span>,
                            OperationKey.DOWNLOAD_CUSTOMIZATION,
                        )}
                    </MenuItem>
                ) : null}
            </MenuItemGroup>

            {![
                OrderTab.SHIPPED,
                OrderTab.SHIPPED_REFUNDING,
                OrderTab.SUCCESS,
                OrderTab.CANCELED,
                OrderTab.PENDING_SHIPMENT_REFUNDING,
            ].includes(tab) ? (
                <MenuItemGroup className="doubleGroup">
                    {isRetailer ? (
                        <MenuItemGroup title="分销操作">
                            {hasKey(OperationKey.BATCH_SUBMIT_ORDER) ? (
                                <MenuItem key={OperationKey.BATCH_SUBMIT_ORDER}>
                                    {renderMenuItemContent(
                                        <span>{OperationLabelNameMap[OperationKey.BATCH_SUBMIT_ORDER]}</span>,
                                        OperationKey.BATCH_SUBMIT_ORDER,
                                    )}
                                </MenuItem>
                            ) : null}

                            {hasKey(OperationKey.PUSH_ORDER_TO_FACTORY) ? (
                                <MenuItem key={OperationKey.PUSH_ORDER_TO_FACTORY}>
                                    {renderMenuItemContent(
                                        <span>{OperationLabelNameMap[OperationKey.PUSH_ORDER_TO_FACTORY]}</span>,
                                        OperationKey.PUSH_ORDER_TO_FACTORY,
                                    )}
                                </MenuItem>
                            ) : null}

                            {hasKey(OperationKey.BATCH_PAY_ORDER) ? (
                                <MenuItem key={OperationKey.BATCH_PAY_ORDER}>
                                    {renderMenuItemContent(
                                        <span>{OperationLabelNameMap[OperationKey.BATCH_PAY_ORDER]}</span>,
                                        OperationKey.BATCH_PAY_ORDER,
                                    )}
                                </MenuItem>
                            ) : null}

                            {hasKey(OperationKey.BATCH_REFUND) ? (
                                <MenuItem key={OperationKey.BATCH_REFUND}>
                                    {renderMenuItemContent(
                                        <span>{OperationLabelNameMap[OperationKey.BATCH_REFUND]}</span>,
                                        OperationKey.BATCH_REFUND,
                                    )}
                                </MenuItem>
                            ) : null}
                        </MenuItemGroup>
                    ) : null}

                    <MenuItemGroup title="其他操作">
                        {hasKey(OperationKey.BATCH_LOCK) ? (
                            <MenuItem key={OperationKey.BATCH_LOCK}>
                                {renderMenuItemContent(
                                    <span>{OperationLabelNameMap[OperationKey.BATCH_LOCK]}</span>,
                                    OperationKey.BATCH_LOCK,
                                )}
                            </MenuItem>
                        ) : null}

                        {hasKey(OperationKey.BATCH_UNLOCK) ? (
                            <MenuItem key={OperationKey.BATCH_UNLOCK}>
                                {renderMenuItemContent(
                                    <Tooltip
                                        placement="right"
                                        title="将当前页面所有已经锁定的订单批量解锁。手动锁定的订单解锁后不再锁定；不可达锁单、地址异常锁单和黑名单锁单解锁后再刷新依然锁定"
                                    >
                                        <span>{OperationLabelNameMap[OperationKey.BATCH_UNLOCK]}</span>
                                    </Tooltip>,
                                    OperationKey.BATCH_UNLOCK,
                                )}
                            </MenuItem>
                        ) : null}

                        {hasKey(OperationKey.BATCH_UPDATE_SHIPPER) ? (
                            <MenuItem key={OperationKey.BATCH_UPDATE_SHIPPER}>
                                {renderMenuItemContent(
                                    <span>{OperationLabelNameMap[OperationKey.BATCH_UPDATE_SHIPPER]}</span>,
                                    OperationKey.BATCH_UPDATE_SHIPPER,
                                )}
                            </MenuItem>
                        ) : null}

                        {hasKey(OperationKey.COPY_ORDER_INFO) ? (
                            <MenuItem key={OperationKey.COPY_ORDER_INFO}>
                                {renderMenuItemContent(
                                    <span>{OperationLabelNameMap[OperationKey.COPY_ORDER_INFO]}</span>,
                                    OperationKey.COPY_ORDER_INFO,
                                    <CopyOrderInfoSettingButton />,
                                )}
                            </MenuItem>
                        ) : null}
                    </MenuItemGroup>
                </MenuItemGroup>
            ) : (
                <MenuItemGroup title="其他操作">
                    {hasKey(OperationKey.COPY_ORDER_INFO) ? (
                        <MenuItem key={OperationKey.COPY_ORDER_INFO}>
                            {renderMenuItemContent(
                                <span>{OperationLabelNameMap[OperationKey.COPY_ORDER_INFO]}</span>,
                                OperationKey.COPY_ORDER_INFO,
                                <CopyOrderInfoSettingButton />,
                            )}
                        </MenuItem>
                    ) : null}
                </MenuItemGroup>
            )}
        </>)} />
    );
};

export default observer(OtherOperationMenu);
